import { ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import NoItems from '@/components/ui/NoItems.tsx';

interface ChangePackageFlowStepGateProps<T> {
  isLoading: boolean;
  /** Szkielet w układzie tego kroku, nie wspólny placeholder. */
  skeleton: ReactNode;
  data: T | null | undefined;
  /**
   * Odmowa backendu — podgląd paczki potrafi rzucić walidacją transportu.
   * Powodem jest zdanie z backendu i to ono ma trafić na ekran, bo tylko ono
   * mówi operatorowi, co poprawić.
   */
  error?: Error | null;
  errorTitle?: string;
  emptyTitle: string;
  emptyDescription: string;
  children: (data: T) => ReactNode;
}

/**
 * Cztery stany kroku przepływu: ładowanie, odmowa, brak danych, treść.
 *
 * Każdy krok czyta inne zapytanie, ale rozstrzyga tak samo, więc rozstrzygnięcie
 * stoi w jednym miejscu, a krok dostaje już pewne dane — bez sprawdzania `null`
 * w środku ekranu. Pusta odpowiedź nie jest błędem: backend potrafi nie mieć
 * czego pokazać, a operator ma się dowiedzieć, dlaczego.
 */
const ChangePackageFlowStepGate = <T,>({
  isLoading,
  skeleton,
  data,
  error,
  errorTitle = 'Nie udało się przeliczyć paczki',
  emptyTitle,
  emptyDescription,
  children,
}: ChangePackageFlowStepGateProps<T>): ReactNode => {
  if (isLoading) {
    return skeleton;
  }
  if (error) {
    return (
      <NoItems
        icon={<TriangleAlert />}
        title={errorTitle}
        description={error.message}
      />
    );
  }
  if (!data) {
    return <NoItems title={emptyTitle} description={emptyDescription} />;
  }
  return children(data);
};

export default ChangePackageFlowStepGate;
